import {
  useState,
  useEffect,
  useMemo,
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactNode,
} from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React TS themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React TS routes
import routes from "routes";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import greenLogo from "assets/images/logos/green-logo.svg";
import whiteLogo from "assets/images/logos/white-logo.svg";
import darkLogo from "assets/images/logos/dark-logo.svg";

// import Login from "./layouts/authentication/sign-in/basic";
import Login from "layouts/authentication/sign-in/basic";
import MemberList from "layouts/management/members/member-list";
// interface PrivateRouteProps {
//   path: string;
//   element: ReactElement<any, string | JSXElementConstructor<any>>;
//   isLoggedIn: boolean;
// }

// const PrivateRoute: React.FC<PrivateRouteProps> = ({ element, isLoggedIn, ...rest }) => (
//   <Route {...rest} element={isLoggedIn ? element : <Navigate to="/login" />} />
// );

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const showAll = localStorage.getItem("allContent") === "true";
  const userId = localStorage.getItem("userId");
  const filteredRoutes = showAll
    ? routes
    : routes
        .map((route) => {
          const allowedKeys =
            userId === "zipgo01"
              ? [
                  "dashboard",
                  "payment",
                  "promotion",
                  "members",
                  "coupon",
                  "consultation",
                  "consultation-detail",
                  "strategy",
                  "strategy-detail",
                  "logout",
                ]
              : [
                  "dashboard",
                  "payment",
                  "promotion",
                  "consultation",
                  "consultation-detail",
                  "strategy",
                  "strategy-detail",
                  "logout",
                ];
          const allowedCouponChildren = ["coupon-issue-list"];

          // 쿠폰 메뉴면 자식만 선별 (zipgo01은 전체 쿠폰 메뉴 표시)
          if ((route.type === "collapse" || route.type === "route") && route.key === "coupon") {
            if (userId === "zipgo01") return route;
            const collapse =
              route.collapse?.filter((item) => allowedCouponChildren.includes(item.key)) ?? [];
            return collapse.length ? { ...route, collapse } : null;
          }

          // 일반 메뉴 필터링
          if (route.type === "collapse" || route.type === "route") {
            return allowedKeys.includes(route.key as string) ? route : null;
          }

          return route;
        })
        .filter(Boolean);

  // Cache for the rtl
  useMemo(() => {
    const pluginRtl: any = rtlPlugin;
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [pluginRtl],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map(
      (route: {
        collapse: any;
        route: string;
        component: ReactElement<any, string | JSXElementConstructor<any>>;
        key: Key;
      }) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          return <Route path={route.route} element={route.component} key={route.key} />;
        }

        return null;
      }
    );
  const routesWithLogin = [
    ...filteredRoutes,
    { route: "", element: <Navigate to="/login" />, key: "login" },
  ];

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );
  function isLoggedIn() {
    // 로그인 상태 확인 로직 작성
    const token = localStorage.getItem("accessToken"); // 로컬 스토리지에서 토큰 가져오기 예시

    if (token) {
      return true;
    } else {
      return false;
    }
  }
  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? greenLogo : whiteLogo}
              brandName="TransFarmerAdmin"
              routes={filteredRoutes}
              // onMouseEnter={handleOnMouseEnter}
              // onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routesWithLogin)}
          {/* <Route
            path="/"
            element={
              isLoggedIn ? <Navigate to="/dashboards/analytics" /> : <Navigate to="/login" />
            }
          /> */}
          {/* <Route path="/login" element={<Login />} key="login" /> */}
          {/* <Route path="*" element={<Navigate to="/dashboards/analytics" />} /> */}
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? greenLogo : whiteLogo}
            brandName="대시보드"
            routes={filteredRoutes}
            // onMouseEnter={handleOnMouseEnter}
            // onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routesWithLogin)}
        <Route
          path="*"
          element={
            isLoggedIn() ? <Navigate to="/management/dashboard" /> : <Navigate to="/login" />
          }
        />
        <Route path="/login" element={<Login />} key="login" />
      </Routes>
    </ThemeProvider>
  );
}
